// Algorithm to handle range overlaps
// IMPORTANT: max value is exclusive - range [1-10] means 1 <= amount < 10
// null maxReceiveAmount means unbounded (infinity)
export const processRangeWithOverlap = (existingRanges, newRange) => {
  // Validate new range
  if (newRange.maxReceiveAmount !== null && newRange.minReceiveAmount >= newRange.maxReceiveAmount) {
    throw new Error('Invalid range: minimum must be less than maximum');
  }
  
  if (!existingRanges || existingRanges.length === 0) {
    return [newRange];
  }
  
  // Sort existing ranges by minReceiveAmount
  const sortedRanges = [...existingRanges].sort((a, b) => a.minReceiveAmount - b.minReceiveAmount);
  const result = [];
  const { minReceiveAmount: newMin, maxReceiveAmount: newMax, etaInMinutes: newEta } = newRange;
  
  let newRangeInserted = false;
  
  for (let range of sortedRanges) {
    const { minReceiveAmount: currMin, maxReceiveAmount: currMax, etaInMinutes: currEta } = range;
    
    // If new range has null max (unbounded), it replaces everything from its min onward
    if (newMax === null) {
      if (currMax === null || currMin >= newMin) {
        // Skip this range as it's covered by the new unbounded range
        if (!newRangeInserted) {
          result.push(newRange);
          newRangeInserted = true;
        }
        continue;
      } else if (currMax > newMin) {
        // Split the current range
        result.push({
          minReceiveAmount: currMin,
          maxReceiveAmount: newMin,
          etaInMinutes: currEta
        });
        if (!newRangeInserted) {
          result.push(newRange);
          newRangeInserted = true;
        }
        continue;
      }
    }
    
    // Case 1: No overlap - current range ends before or at new range start
    if (currMax !== null && currMax <= newMin) {
      result.push(range);
    }
    // Case 2: No overlap - current range starts at or after new range end (newMax not null)
    else if (newMax !== null && currMin >= newMax) {
      // Insert new range before this range (only once)
      if (!newRangeInserted) {
        result.push(newRange);
        newRangeInserted = true;
      }
      result.push(range);
    }
    // Case 3: Overlap exists - need to split the current range
    else {
      // Add the part before overlap (if exists)
      if (currMin < newMin) {
        result.push({
          minReceiveAmount: currMin,
          maxReceiveAmount: newMin, // Set max to where new range starts
          etaInMinutes: currEta
        });
      }
      
      // Add the new range (only once)
      if (!newRangeInserted) {
        result.push(newRange);
        newRangeInserted = true;
      }
      
      // Add the part after overlap (if exists and new range is bounded)
      if (newMax !== null && currMax !== null && currMax > newMax) {
        result.push({
          minReceiveAmount: newMax, // Start where new range ends
          maxReceiveAmount: currMax,
          etaInMinutes: currEta
        });
      } else if (newMax !== null && currMax === null) {
        // Current range is unbounded, keep the part after new range
        result.push({
          minReceiveAmount: newMax,
          maxReceiveAmount: null,
          etaInMinutes: currEta
        });
      }
    }
  }
  
  // If new range wasn't inserted yet (it's after all existing ranges)
  if (!newRangeInserted) {
    result.push(newRange);
  }
  
  return result;
};
